import { useEffect, useRef } from "react";
import { DialogProps } from "../../types/dialog-types";
import { tagSelectStyles as s } from "./tagSelectDialogStyles";
import { getTigerResource } from "../../utils/tigerResource";

/**
 * How close to the bottom still counts as "following" the list, in px. Two
 * lines' worth, so a stray wheel notch does not unstick it.
 */
const FOLLOW_SLACK_PX = 24;

// Port of the C++ Ui_progress_info dialog (Ui_progress_info_dialog_start):
// a modal busy indicator shown while a long operation runs. It has no close
// button — the caller dismisses it when the work completes. Themed to match
// the Graph Channel Selection dialog.
export interface UIProgressDialogProps extends DialogProps {
  title?: string;
  message?: string;
  /**
   * Optional step list shown under `message`, each line ticking as it
   * completes (see useStartupProgress). A long operation that knows its own
   * beats is far easier to tell apart from a hang; callers with nothing to
   * report leave this out and get the plain spinner.
   */
  steps?: { id: string; text: string; done: boolean }[];
}

export function UIProgressDialog({
  open,
  title = getTigerResource("TIGER_RES_PLEASE_WAIT"),
  message = getTigerResource("TIGER_RES_PLEASE_WAIT"),
  steps,
}: UIProgressDialogProps) {
  const listRef = useRef<HTMLUListElement | null>(null);
  // Whether to keep following the bottom. True until the user scrolls up: the
  // list is capped and a multi-turbine version check pushes the running step
  // out of sight, so the useful view is the newest line — but yanking the view
  // back while someone is reading an earlier one would be worse than not
  // scrolling at all.
  const followRef = useRef(true);

  // Declared before the `open` early-return below so the hook order is stable
  // across an open/closed render (rules of hooks). It no-ops while closed,
  // when there is no list element to scroll.
  useEffect(() => {
    const el = listRef.current;
    if (!el || !followRef.current) return;
    el.scrollTop = el.scrollHeight;
  }, [steps]);

  if (!open) return null;

  return (
    <div style={s.overlay}>
      <div
        style={{
          ...s.dialog,
          width: 340,
          height: "auto",
          minHeight: 0,
          resize: "none",
          top: "calc(50vh - 90px)",
          left: "max(2.5vw, calc(50vw - 170px))",
        }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div style={s.header}>
          <span style={s.title}>{title}</span>
        </div>

        <div style={{ ...s.body, alignItems: "center", textAlign: "center" }}>
          <span
            aria-label={getTigerResource("TIGER_RES_BUSY")}
            style={{ fontSize: 28, animation: "spin 1.5s linear infinite" }}
          >
            &#8987;
          </span>
          <p style={{ margin: 0, fontSize: 13, color: "#374151", whiteSpace: "pre-line" }}>
            {message}
          </p>
          {steps && steps.length > 0 && (
            // Capped and scrollable: a multi-turbine site runs one step per
            // config (17 on tslalarm), which would otherwise grow the dialog
            // off the screen. Newest is at the bottom, so the running step is
            // what a scrolled-to-end list shows.
            <ul
              ref={listRef}
              aria-label={getTigerResource("TIGER_UI_CONNECTING_SERVER_TITLE")}
              onScroll={() => {
                const el = listRef.current;
                if (!el) return;
                followRef.current =
                  el.scrollHeight - el.scrollTop - el.clientHeight <= FOLLOW_SLACK_PX;
              }}
              style={{
                listStyle: "none",
                margin: "4px 0 0",
                padding: "8px 10px",
                width: "100%",
                boxSizing: "border-box",
                maxHeight: 168,
                overflowY: "auto",
                textAlign: "left",
                fontSize: 12,
                lineHeight: 1.6,
                color: "#374151",
                background: "rgba(0,0,0,0.03)",
                borderRadius: 4,
              }}
            >
              {steps.map((s) => (
                <li key={s.id} style={{ display: "flex", gap: 6, alignItems: "baseline" }}>
                  <span
                    aria-hidden="true"
                    style={{
                      flex: "0 0 auto",
                      width: "1em",
                      color: s.done ? "#15803d" : "#6b7280",
                      animation: s.done ? undefined : "spin 1.5s linear infinite",
                    }}
                  >
                    {s.done ? "✓" : "⧗"}
                  </span>
                  <span style={{ opacity: s.done ? 0.75 : 1 }}>{s.text}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
